import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { DbChallenge } from '@/hooks/useChallengeLab';
import { Target, Users, TrendingUp, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface ChallengeInfoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  challenge: DbChallenge | null;
}

const statusLabels: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' }> = {
  draft: { label: 'Borrador', variant: 'outline' },
  active: { label: 'Identificado', variant: 'secondary' },
  completed: { label: 'Completado', variant: 'default' },
};

export function ChallengeInfoModal({ open, onOpenChange, challenge }: ChallengeInfoModalProps) {
  if (!challenge) return null;

  const status = statusLabels[challenge.status || 'draft'] || statusLabels.draft;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center shrink-0">
              <Target className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-lg leading-tight">
                {challenge.title || 'Reto en progreso...'}
              </DialogTitle>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant={status.variant}>{status.label}</Badge>
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {format(new Date(challenge.created_at), "d MMM yyyy", { locale: es })}
                </span>
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {challenge.description && (
            <div className="space-y-1.5">
              <h4 className="text-sm font-medium text-muted-foreground">Problema</h4>
              <p className="text-sm">{challenge.description}</p>
            </div>
          )}

          {challenge.target_audience && (
            <div className="space-y-1.5">
              <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                <Users className="w-4 h-4" />
                Público Objetivo
              </h4>
              <p className="text-sm">{challenge.target_audience}</p>
            </div>
          )}

          {challenge.kpi_name && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                <TrendingUp className="w-4 h-4" />
                KPI de Impacto
              </h4>
              <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                <p className="text-sm font-medium">{challenge.kpi_name}</p>
                {(challenge.kpi_base_value || challenge.kpi_expected_value) && (
                  <div className="flex items-center gap-2 text-sm">
                    {challenge.kpi_base_value && (
                      <span className="text-muted-foreground">
                        Base: <span className="text-foreground font-medium">{challenge.kpi_base_value}</span>
                      </span>
                    )}
                    {challenge.kpi_base_value && challenge.kpi_expected_value && <span className="text-muted-foreground">→</span>}
                    {challenge.kpi_expected_value && (
                      <span className="text-muted-foreground">
                        Esperado: <span className="text-primary font-medium">{challenge.kpi_expected_value}</span>
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {challenge.status === 'draft' && !challenge.description && (
            <div className="text-center py-6 text-muted-foreground">
              <Target className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">El reto aún está siendo identificado.</p>
              <p className="text-xs">Continúa la conversación con el asistente.</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
